// src/services/monthlyAutosReportService.js
/**
 * Aggregates invoices + items into the Monthly Autos report shape used by MonthlyAutosDashboardPage
 * and MonthlySaleReportPage (via mapMonthlyAutosReportToSalePageData).
 *
 * Loads invoices for the report window via `invoiceDateStr` range (prev + current month).
 * Loads only item master rows for product codes that appear on those invoices.
 */
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { normalizeBranchValue } from "../config/appBranches";
import {
  buildHolidayYmdSetFromDocs,
  getWorkingDayYmdsFullMonth,
} from "../helpers/payrollAttendanceCore";
import {
  creditBillBranch,
  fetchAllCreditBills,
  fetchAllPayments,
  paymentTimestamp,
} from "./creditDueService";
import {
  normalizeVoucherType,
  lineBillAmountHeaderOnly,
  lineAmountOnly,
  resolveImportVoucherTotal,
} from "../features/salesDataImport/services/salesLineChunksReader";
import {
  isCreditNoteVoucherType,
  appBranchFromImportRow,
  filterImportRowsForMonthlyReport,
  groupImportRowsAsInvoiceDocs,
  loadSalesImportRows,
  includeRowInMonthlyImportReport,
  includeRowInMonthlyImportReportPrevMonth,
  clearSalesImportRowsCache,
  isTransferOutNewVoucherRow,
  isBranchTransferVoucherRow,
  isTransferOutDirectionVoucherRow,
  isTransferInDirectionVoucherRow,
  isWinsBranchSDebtorParty,
  isMainBranchImportSaleVoucherRow,
  isWholesaleImportSaleVoucherRow,
} from "./salesImportTransactionDataService";

export const MONTHLY_AUTOS_BRANDS = [
  "HERO",
  "HONDA",
  "BAJAJ",
  "BULLET",
  "TVS",
  "SUZUKI",
  "YAMAHA",
  "MAHINDRA",
  "PIAGGIO",
];

/** Title line uses title-case month (e.g. "April 2026"). */
const REPORT_TITLE_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Display name in report title (matches MonthlySaleReportPage branch dropdown). */
const MONTHLY_REPORT_BRANCH_TITLE = {
  Main: "Main Branch",
  "Branch 2": "Spareway",
  "Branch 3": "Branch",
  Wholesale: "Wholesale",
};

const MONTHLY_IMPORT_BRANCHES = ["Main", "Branch 2", "Branch 3", "Wholesale"];

/**
 * Parse invoice date in local calendar terms so month windows (monthWindow / prevMonthWindow)
 * match Firestore `invoiceDateStr` like "2026-04-15". `new Date("2026-04-15")` is UTC midnight
 * and can fall on the wrong local day near zone boundaries.
 */
function parseInvoiceDate(inv) {
  const coerceTimestampLike = (raw) => {
    if (!raw) return null;
    if (typeof raw.toDate === "function") {
      const d = raw.toDate();
      if (Number.isNaN(d.getTime())) return null;
      // Normalize to local noon to prevent month-boundary shifts.
      return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0);
    }
    // Firestore timestamp sometimes comes back as a plain object (e.g. from backups/exports)
    // { seconds, nanoseconds } or { _seconds, _nanoseconds }
    const secs =
      (typeof raw.seconds === "number" && raw.seconds) ||
      (typeof raw._seconds === "number" && raw._seconds) ||
      null;
    if (secs != null) {
      const d = new Date(secs * 1000);
      if (Number.isNaN(d.getTime())) return null;
      return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0);
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0);
  };

  const parseDateStringLocal = (input) => {
    if (input == null) return null;
    const s = String(input).trim();
    if (!s) return null;

    // yyyy-mm-dd / yyyy-m-d / yyyy/mm/dd
    let m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (m) {
      const y = Number(m[1]);
      const mo = Number(m[2]) - 1;
      const day = Number(m[3]);
      const d = new Date(y, mo, day, 12, 0, 0, 0);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    // dd/mm/yyyy | dd-mm-yyyy
    m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (m) {
      const day = Number(m[1]);
      const mo = Number(m[2]) - 1;
      const y = Number(m[3]);
      const d = new Date(y, mo, day, 12, 0, 0, 0);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    // dd/mm/yy | dd-mm-yy (assume 20yy)
    m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2})$/);
    if (m) {
      const day = Number(m[1]);
      const mo = Number(m[2]) - 1;
      const yy = Number(m[3]);
      const y = 2000 + yy;
      const d = new Date(y, mo, day, 12, 0, 0, 0);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    // Last resort: Date() parse, then normalize to local noon
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0);
  };

  const pick = (raw) => {
    const t = coerceTimestampLike(raw);
    if (t) return t;
    if (typeof raw === "string") return parseDateStringLocal(raw);
    return null;
  };

  // Try common fields across app versions.
  const candidates = [
    inv?.invoiceDateStr,
    inv?.invoiceDate,
    inv?.date,
    inv?.tranDate,
    inv?.dateString,
    inv?.createdAt,
    inv?.createdOn,
    inv?.created,
  ];

  for (const raw of candidates) {
    const d = pick(raw);
    if (d) return d;
  }

  if (typeof inv === "string" || typeof inv === "number") {
    const d = pick(inv);
    if (d) return d;
  }

  return null;
}

/** Brand column for a line — same rules for current and previous month. */
function brandForLine(it, code, codeToBrand, codeToRawBrand) {
  const lineBrand =
    it.brandName != null && String(it.brandName).trim() !== ""
      ? String(it.brandName).trim()
      : it.brand != null && String(it.brand).trim() !== ""
        ? String(it.brand).trim()
        : it.mfr != null && String(it.mfr).trim() !== ""
          ? String(it.mfr).trim()
          : "";
  if (lineBrand && !isNonBrandHint(lineBrand)) {
    const oeB = oeBrandFromBrandField(lineBrand);
    if (oeB) return oeB;
    const fromLine = normalizeBrand(lineBrand);
    if (fromLine !== "MISCELLANEOUS") return fromLine;
  }

  const masterRaw = codeToRawBrand[code] || "";
  if (masterRaw) {
    const fromMaster = normalizeBrand(masterRaw);
    if (fromMaster !== "MISCELLANEOUS") return fromMaster;
  }
  if (codeToBrand[code] && codeToBrand[code] !== "MISCELLANEOUS") return codeToBrand[code];

  for (const text of [it.alias, it.desc, it.itemName]) {
    const s = String(text || "").trim();
    if (!s) continue;
    const embedded = brandFromEmbeddedText(s);
    if (embedded) return embedded;
    const fromText = normalizeBrand(s);
    if (fromText !== "MISCELLANEOUS") return fromText;
  }
  return "MISCELLANEOUS";
}

/** Raw brand text for OE vs ordinary — import BrandName (OE*) wins over items master. */
function rawBrandForOeLine(it, code, codeToRawBrand) {
  for (const c of [it.brandName, it.brand, it.mfr]) {
    const s = String(c || "").trim();
    if (!s || isNonBrandHint(s)) continue;
    if (isOePrefixedBrandText(s)) return s;
  }
  for (const c of [codeToRawBrand[code], it.alias, it.desc]) {
    const s = String(c || "").trim();
    if (!s || isNonBrandHint(s)) continue;
    if (oeBrandFromBrandField(s)) return s;
  }
  return String(it.brandName || it.brand || it.mfr || codeToRawBrand[code] || "").trim();
}

function oeBucketForImportLine(rawBrand, brand) {
  const fromField = oeBrandFromBrandField(rawBrand);
  if (fromField) return fromField;
  if (isOePrefixedBrandText(rawBrand) && brand && MONTHLY_AUTOS_BRANDS.includes(brand)) {
    return brand;
  }
  return null;
}

function lineCode(it) {
  return (it?.code || it?.itemCode || it?.item_code || it?.itemcode || "")
    .toString()
    .trim();
}

/**
 * Branch 3 import report: transfer IN lines add to brand purchase (same as manual upload).
 * Allocates voucher total across line brands (BillAmount when line Amount is zero).
 */
function addBranch3TransferInToPurchaseBucket(bucket, inv, codeToBrand, codeToRawBrand) {
  if (bucket.miscPurchase == null) bucket.miscPurchase = 0;

  const voucherTotal = Number(inv.grandTotal) || 0;
  if (!(voucherTotal > 0)) return;

  const items = inv.items || [];
  if (!items.length) {
    bucket.miscPurchase += voucherTotal;
    return;
  }

  const parts = items.map((it) => {
    const code = lineCode(it);
    const amt = Math.abs(Number(it.importLineAmount) || 0);
    const brand = brandForLine(it, code, codeToBrand, codeToRawBrand);
    return { brand, amt };
  });

  const withAmt = parts.filter((p) => p.amt > 0);
  if (!withAmt.length) {
    const brands = [
      ...new Set(
        parts
          .map((p) => p.brand)
          .filter((b) => b && MONTHLY_AUTOS_BRANDS.includes(b))
      ),
    ];
    if (!brands.length) {
      bucket.miscPurchase += voucherTotal;
      return;
    }
    const share = voucherTotal / brands.length;
    for (const brand of brands) bucket.purchaseByBrand[brand] += share;
    return;
  }

  const partsSum = withAmt.reduce((s, p) => s + p.amt, 0);
  const scale = partsSum > voucherTotal * 1.001 ? voucherTotal / partsSum : 1;
  for (const { brand, amt } of withAmt) {
    const share = amt * scale;
    if (brand === "MISCELLANEOUS") bucket.miscPurchase += share;
    else if (MONTHLY_AUTOS_BRANDS.includes(brand)) bucket.purchaseByBrand[brand] += share;
    else bucket.miscPurchase += share;
  }
}

function inRange(d, start, end) {
  if (!d) return false;
  return d >= start && d <= end;
}

/**
 * Fallback when an outward invoice has no line items: use transaction class.
 */
function classifySaleKind(inv) {
  const tc = (inv?.transactionClass || "").toString().toUpperCase();
  if (tc.includes("ORDINARY") || tc.includes("OTHER SALE")) return "ordinary";
  if (/\bOE\b|O\.E|O\/E/.test(tc) || tc.includes("OE")) return "oe";
  return "ordinary";
}

/**
 * Brand/mfr starting with OE → OE sale; everything else → ordinary (e.g. "OE HERO", "OEHERO", "HERO").
 * @returns {string|null} brand key for OE bucket, or null if not OE-prefixed
 */
export function oeBrandFromBrandField(rawBrand) {
  const s = (rawBrand || "").toString().toUpperCase().replace(/\s+/g, " ").trim();
  if (!/^OE/.test(s)) return null;
  const rest = s.slice(2).replace(/^[\s.\-_/]+/, "").trim();
  if (!rest) return null;
  for (const b of MONTHLY_AUTOS_BRANDS) {
    if (rest === b || rest.startsWith(`${b} `) || rest.startsWith(`${b}-`)) return b;
  }
  const n = normalizeBrand(rest);
  return n !== "MISCELLANEOUS" && MONTHLY_AUTOS_BRANDS.includes(n) ? n : null;
}

function lineNetAmount(it, isInterState) {
  const importLine = Number(it.importLineAmount);
  if (Number.isFinite(importLine) && importLine !== 0) {
    return Math.abs(importLine);
  }
  const qty = parseFloat(it.qty) || 0;
  const rate = parseFloat(it.rate) || 0;
  const disc = parseFloat(it.discount) || 0;
  const gstVal = it?.gst ?? it?.GST ?? 12;
  const gst = parseFloat(gstVal) || 12;
  const gross = qty * rate;
  const discAmt = (gross * disc) / 100;
  const netBeforeTax = gross - discAmt;
  if (isInterState) {
    const igstAmt = (netBeforeTax * gst) / 100;
    return netBeforeTax + igstAmt;
  }
  const half = gst / 2;
  const sgstAmt = (netBeforeTax * half) / 100;
  return netBeforeTax + sgstAmt * 2;
}

export function normalizeBrand(raw) {
  const s = (raw || "").toString().toUpperCase().trim();
  if (!s) return "MISCELLANEOUS";
  for (const b of MONTHLY_AUTOS_BRANDS) {
    if (s === b || s.includes(b) || b.includes(s)) return b;
  }
  return "MISCELLANEOUS";
}

/** Profit centre / payment labels — not vehicle brands. */
const NON_BRAND_HINTS = new Set([
  "CREDIT",
  "CASH",
  "DEBIT",
  "UDHAR",
  "SALE",
  "PURCHASE",
  "SALES",
]);

function isNonBrandHint(value) {
  const s = String(value || "").toUpperCase().trim();
  return !s || NON_BRAND_HINTS.has(s);
}

/** Find a known brand name embedded in alias / description text. */
function brandFromEmbeddedText(text) {
  const s = String(text || "").toUpperCase();
  if (!s) return null;
  const sorted = [...MONTHLY_AUTOS_BRANDS].sort((a, b) => b.length - a.length);
  for (const b of sorted) {
    if (s.includes(b)) return b;
  }
  return null;
}

/** BrandName column (e.g. "OE BAJAJ", "BAJAJ") — not Item Group / profit centre. */
function importRowBrandName(row) {
  const candidates = [
    row?.brandName,
    row?.BrandName,
    row?.brandname,
    row?.["Brand Name"],
  ];
  for (const c of candidates) {
    const s = String(c ?? "").trim();
    if (!s || isNonBrandHint(s)) continue;
    return s;
  }
  return "";
}

function isOePrefixedBrandText(raw) {
  const s = String(raw || "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
  return /^OE/.test(s);
}

function importCreditNoteLineAmount(row) {
  const fromAmount = lineAmountOnly(row);
  if (fromAmount !== 0) return Math.abs(fromAmount);
  const qty = Number(row.qty) || 0;
  const rate = Number(row.unitPrice) || 0;
  if (qty > 0 && rate > 0) return Math.abs(qty * rate);
  return 0;
}

function resolveImportRowBrand(row, codeToBrand, codeToRawBrand) {
  const brandName = importRowBrandName(row);
  if (brandName) {
    const oeB = oeBrandFromBrandField(brandName);
    if (oeB) return { brand: oeB, isOe: true };
    const b = normalizeBrand(brandName);
    if (b !== "MISCELLANEOUS") {
      return { brand: b, isOe: isOePrefixedBrandText(brandName) };
    }
  }

  const partNo = String(row.partNo || "").trim();

  if (partNo && codeToRawBrand[partNo]) {
    const raw = codeToRawBrand[partNo];
    const oeB = oeBrandFromBrandField(raw);
    if (oeB) return { brand: oeB, isOe: true };
    if (codeToBrand[partNo] && codeToBrand[partNo] !== "MISCELLANEOUS") {
      return { brand: codeToBrand[partNo], isOe: false };
    }
  }

  for (const h of [row.itemGroup, row.alias, row.itemName]) {
    const s = String(h || "").trim();
    if (!s) continue;
    const oeB = oeBrandFromBrandField(s);
    if (oeB) return { brand: oeB, isOe: true };
    const embedded = brandFromEmbeddedText(s);
    if (embedded) return { brand: embedded, isOe: false };
  }

  return { brand: null, isOe: false };
}

function brandFromTransactionClass(inv) {
  const tc = (inv?.transactionClass || "").toString().toUpperCase();
  if (!tc) return null;
  for (const b of MONTHLY_AUTOS_BRANDS) {
    if (tc.includes(b)) return b;
  }
  const n = normalizeBrand(tc);
  return n !== "MISCELLANEOUS" ? n : null;
}

function sumLineNets(items, isInterState) {
  let s = 0;
  for (const it of items || []) {
    s += lineNetAmount(it, isInterState);
  }
  return s;
}

function allocateLineShare(inv, it, isInterState) {
  const items = inv.items || [];
  const sum = sumLineNets(items, isInterState);
  const lineNet = lineNetAmount(it, isInterState);
  const gt = Number(inv.grandTotal ?? inv.totals?.net ?? 0) || 0;
  if (sum <= 0) return items.length ? gt / items.length : 0;
  return (lineNet / sum) * gt;
}

function importVoucherLineSum(inv) {
  return (inv.items || []).reduce((s, it) => s + (Number(it.importLineAmount) || 0), 0);
}

function importOsCsSaleAmounts(inv, gt) {
  const lineSum = importVoucherLineSum(inv);
  const billAmt = Math.abs(Number(inv.billAmount) || 0);
  const saleAmt = lineSum > 0 ? lineSum : billAmt || gt;
  const billTotal = billAmt > 0 ? billAmt : saleAmt;
  return { saleAmt, billTotal };
}

function isMainBranchTransferOutPurchaseInvoice(inv) {
  if (inferInvoiceBranch(inv) !== "Main") return false;
  return (
    isTransferOutNewVoucherRow({ voucherType: inv.transactionClass }) &&
    isWinsBranchSDebtorParty({ customerName: inv.party ?? inv.partyName })
  );
}

function subtractPurchaseByBrand(bucket, inv, isInterState, codeToBrand, codeToRawBrand) {
  for (const it of inv.items || []) {
    const code = lineCode(it);
    const brand = brandForLine(it, code, codeToBrand, codeToRawBrand);
    const share = allocateLineShare(inv, it, isInterState);
    if (brand === "MISCELLANEOUS") bucket.miscPurchase -= share;
    else if (MONTHLY_AUTOS_BRANDS.includes(brand)) {
      bucket.purchaseByBrand[brand] -= share;
    }
  }
}

function monthWindow(year, monthIndex) {
  const start = new Date(year, monthIndex, 1, 0, 0, 0, 0);
  const end = new Date(year, monthIndex + 1, 0, 23, 59, 59, 999);
  return { start, end };
}

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** Cash received on the invoice at billing time (partial payments on CASH bills). */
function invoiceAmountPaid(inv) {
  if (!inv) return 0;
  if (typeof inv.payment === "number") return roundMoney(inv.payment);
  if (inv.payment && typeof inv.payment.amountPaid === "number") {
    return roundMoney(inv.payment.amountPaid);
  }
  if (inv.payment && typeof inv.payment.amountPaid === "string") {
    return roundMoney(Number(inv.payment.amountPaid));
  }
  if (Array.isArray(inv.payments)) {
    return roundMoney(
      inv.payments.reduce((s, p) => s + (Number(p.amount || p.amountPaid || 0) || 0), 0)
    );
  }
  return roundMoney(inv.amountPaid);
}

function inferPaymentBranch(p) {
  const b = normalizeBranchValue(p?.branch);
  if (b && MONTHLY_IMPORT_BRANCHES.includes(b)) return b;
  return "Main";
}

function inferInvoiceBranch(inv) {
  const raw =
    inv?.branch ??
    inv?.branchName ??
    inv?.branchValue ??
    inv?.shop ??
    inv?.shopName ??
    inv?.location ??
    "";
  const byField = normalizeBranchValue(raw);
  if (byField && MONTHLY_IMPORT_BRANCHES.includes(byField)) return byField;
  const tc = (inv?.transactionClass || "").toString().toUpperCase().replace(/\s+/g, " ");
  if (tc.includes("CREDIT SALES WS") || tc.includes("OUTWARD SUPPLY WS") || tc.includes("WHOLESALE") || /\bWS\b/.test(tc)) {
    return "Wholesale";
  }
  if (tc.includes("SPAREWAY") || tc.includes("SPARE WAY") || tc.includes("BRANCH 11")) return "Branch 2";
  if (tc.includes("BRANCH 10") || tc.includes("BRANCH-10") || tc.includes("BRANCH10")) return "Branch 3";
  if (tc.includes("BRANCH 3") || tc.includes("BRANCH-3") || tc.includes("BRANCH3")) return "Branch 3";
  if (tc.includes("BRANCH 1") || tc.includes("BRANCH-1") || tc.includes("BRANCH1")) return "Main";
  return "Main";
}

function brandFromImportCreditRow(row, codeToBrand, codeToRawBrand) {
  return resolveImportRowBrand(row, codeToBrand, codeToRawBrand);
}

/**
 * Credit-note sale return: brand columns = sum of line Amount by BrandName;
 * voucher total = line amounts capped once per BillAmount header (no bill split to brands).
 */
function creditNoteBrandTotalsForVoucher(bucket) {
  const brandTotals = Object.fromEntries(MONTHLY_AUTOS_BRANDS.map((b) => [b, 0]));
  const oeTotals = Object.fromEntries(MONTHLY_AUTOS_BRANDS.map((b) => [b, 0]));
  const ordTotals = Object.fromEntries(MONTHLY_AUTOS_BRANDS.map((b) => [b, 0]));
  let brandLineSum = 0;
  let allLineSum = 0;

  for (const line of bucket.lines) {
    if (line.amt > 0) allLineSum += line.amt;
    if (!line.brand || !(line.amt > 0)) continue;
    brandTotals[line.brand] += line.amt;
    if (line.isOe) oeTotals[line.brand] += line.amt;
    else ordTotals[line.brand] += line.amt;
    brandLineSum += line.amt;
  }

  const billHeader = bucket.bill > 0 ? bucket.bill : 0;
  const voucherTotal = resolveImportVoucherTotal(allLineSum, billHeader);
  if (!(voucherTotal > 0)) {
    return { brandTotals, oeTotals, ordTotals, brandSum: 0, voucherTotal: 0 };
  }

  // Line Amount repeated as full bill on every row → scale brand breakdown to one voucher total.
  if (brandLineSum > voucherTotal * 1.001) {
    const scale = voucherTotal / brandLineSum;
    for (const b of MONTHLY_AUTOS_BRANDS) {
      if (brandTotals[b] > 0) {
        brandTotals[b] *= scale;
        oeTotals[b] *= scale;
        ordTotals[b] *= scale;
      }
    }
    brandLineSum = voucherTotal;
  }

  return {
    brandTotals,
    oeTotals,
    ordTotals,
    brandSum: brandLineSum,
    voucherTotal,
  };
}

function collectCreditNotePartNos(rows) {
  const codes = new Set();
  for (const row of rows || []) {
    if (!isCreditNoteVoucherType(normalizeVoucherType(row))) continue;
    const c = String(row.partNo || "").trim();
    if (c) codes.add(c);
  }
  return [...codes];
}

/**
 * Sales return from import Credit Note rows (voucher type + line Amount + BillAmount).
 * Row-level processing is more reliable than grouped invoice docs for credit notes.
 */
function accumulateImportCreditNoteSaleReturns(
  rows,
  { branchFilter, curStart, curEnd, curr, codeToBrand, codeToRawBrand }
) {
  const voucherMap = new Map();

  for (const row of rows || []) {
    const vt = normalizeVoucherType(row);
    if (!isCreditNoteVoucherType(vt)) continue;

    if (appBranchFromImportRow(row) !== branchFilter) continue;

    const d = parseInvoiceDate(row);
    if (!d || !inRange(d, curStart, curEnd)) continue;

    const voucherNo = String(row.voucherNo || "").trim();
    if (!voucherNo) continue;

    const vKey = `${vt}::${voucherNo}`;
    let bucket = voucherMap.get(vKey);
    if (!bucket) {
      bucket = { bill: 0, lines: [] };
      voucherMap.set(vKey, bucket);
    }

    const billHeader = lineBillAmountHeaderOnly(row);
    if (billHeader > bucket.bill) bucket.bill = billHeader;

    const amt = importCreditNoteLineAmount(row);
    const { brand, isOe } = brandFromImportCreditRow(row, codeToBrand, codeToRawBrand);
    bucket.lines.push({ brand, isOe, amt });
  }

  for (const [, bucket] of voucherMap) {
    const { brandTotals, oeTotals, ordTotals, brandSum, voucherTotal } =
      creditNoteBrandTotalsForVoucher(bucket);
    if (!(voucherTotal > 0)) continue;

    // LESS SALE RETURN: one capped total per credit-note voucher.
    curr.lessSR += voucherTotal;

    if (brandSum > 0) {
      for (const b of MONTHLY_AUTOS_BRANDS) {
        if (brandTotals[b] > 0) curr.saleReturnByBrand[b] += brandTotals[b];
        if (oeTotals[b] > 0) curr.saleReturnOeByBrand[b] += oeTotals[b];
        if (ordTotals[b] > 0) curr.saleReturnOrdByBrand[b] += ordTotals[b];
      }
    }
  }
}

function prevMonthWindow(year, monthIndex) {
  const d = new Date(year, monthIndex, 1);
  d.setMonth(d.getMonth() - 1);
  const py = d.getFullYear();
  const pm = d.getMonth();
  return monthWindow(py, pm);
}

function pctChange(prev, curr) {
  if (!prev || Number.isNaN(prev)) return 0;
  return ((curr - prev) / Math.abs(prev)) * 100;
}

function ymdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function collectInvoiceLineCodes(invoices) {
  const codes = new Set();
  for (const inv of invoices) {
    for (const it of inv.items || []) {
      const c = lineCode(it);
      if (c) codes.add(c);
    }
  }
  return [...codes];
}

/** Firestore `in` supports up to 10 values per query. */
const ITEM_CODE_IN_CHUNK = 10;
const ITEM_FETCH_WAVE = 25;

async function fetchItemDocsByCodes(codes) {
  if (!codes.length) return [];
  const chunks = [];
  for (let i = 0; i < codes.length; i += ITEM_CODE_IN_CHUNK) {
    chunks.push(codes.slice(i, i + ITEM_CODE_IN_CHUNK));
  }
  const out = [];
  for (let i = 0; i < chunks.length; i += ITEM_FETCH_WAVE) {
    const wave = chunks.slice(i, i + ITEM_FETCH_WAVE);
    const snaps = await Promise.all(
      wave.map((chunk) =>
        getDocs(query(collection(db, "items"), where("code", "in", chunk)))
      )
    );
    for (const snap of snaps) {
      snap.forEach((doc) => out.push(doc));
    }
  }
  return out;
}

/**
 * Loads invoices for [prevStart, curEnd] in local calendar terms.
 *
 * Firestore range on `invoiceDateStr` only works when every stored value sorts
 * chronologically as strings (zero-padded yyyy-mm-dd). Values like `2026-3-15`
 * sort *after* `2026-04-30`, so March rows can be missing for an April report
 * while April rows still match — previous month columns look empty.
 *
 * We merge with a range query on the ISO `date` field (written at save time),
 * which sorts chronologically; then we still filter each doc with parseInvoiceDate().
 */
async function loadImportRowsForDateSpan(rangeStart, rangeEnd, branchFilter, { excludeCreditNotes = false } = {}) {
  const minStr = ymdLocal(rangeStart);
  const maxStr = ymdLocal(rangeEnd);
  let rowFilter = null;
  let rowFilterKey = "all";
  if (branchFilter === "Main" || branchFilter === "Wholesale") {
    if (excludeCreditNotes) {
      rowFilter = (row) => includeRowInMonthlyImportReportPrevMonth(row, branchFilter);
      rowFilterKey = `${branchFilter === "Wholesale" ? "wholesale" : "main"}-prev`;
    } else {
      rowFilter = (row) => includeRowInMonthlyImportReport(row, branchFilter);
      rowFilterKey = `${branchFilter === "Wholesale" ? "wholesale" : "main"}-full`;
    }
    const rows = await loadSalesImportRows({
      fromDate: minStr,
      toDate: maxStr,
      rowFilter,
      rowFilterKey,
    });
    return rows;
  }
  const rows = await loadSalesImportRows({ fromDate: minStr, toDate: maxStr });
  return filterImportRowsForMonthlyReport(rows, branchFilter);
}

/** @deprecated use loadImportRowsForDateSpan */
async function loadImportRowsForReportWindow(prevStart, curEnd, branchFilter = "Main") {
  return loadImportRowsForDateSpan(prevStart, curEnd, branchFilter);
}

/** In-memory cache for import-only monthly report (same month/branch). */
const importMonthlyReportCache = new Map();

function patchBranch3ImportReportFromPrecalc(report, byBrand) {
  if (!report || !Array.isArray(byBrand) || !byBrand.length) return report;

  const byKey = Object.fromEntries(byBrand.map((r) => [String(r.key || r.label || "").trim(), r]));
  const brands = (report.brands || []).map((b) => {
    const row = byKey[b.name];
    if (!row) return b;
    const purchaseCurr = (Number(row.purchase) || 0) + (Number(row.transferIn) || 0);
    return {
      ...b,
      purchaseCurr,
      purchasePct: pctChange(Number(b.purchasePrev) || 0, purchaseCurr),
    };
  });

  const miscRow = byKey.MISCELLANEOUS;
  const miscPurchase = miscRow
    ? (Number(miscRow.purchase) || 0) + (Number(miscRow.transferIn) || 0)
    : Number(report.footer?.miscPurchase) || 0;
  const brandPurchaseSum = brands.reduce((s, b) => s + (Number(b.purchaseCurr) || 0), 0);
  const totalPurchase = brandPurchaseSum + miscPurchase;

  return {
    ...report,
    brands,
    footer: {
      ...(report.footer || {}),
      miscPurchase,
      totalPurchase,
    },
  };
}

export function clearImportMonthlyReportCache() {
  importMonthlyReportCache.clear();
  clearSalesImportRowsCache();
}

function mapImportRowsToInvoiceDocs(inRange) {
  return groupImportRowsAsInvoiceDocs(inRange).map((inv) => ({
    id: inv.id,
    ...inv,
    invoiceDateStr: String(inv.date || inv.invoiceDateStr || "").slice(0, 10),
    date: inv.date || inv.invoiceDateStr,
  }));
}

/**
 * @returns {Promise<object|null>} Report object or null if Firestore fails / no data path
 */
export async function fetchMonthlyAutosReport(
  year,
  monthIndex,
  branchFilter = "Main",
  { importOnly = false, dateScope = "full", skipPrecalc = false } = {}
) {
  const y = Number(year);
  const mi = Math.max(0, Math.min(11, Number.isFinite(Number(monthIndex)) ? Number(monthIndex) : 0));
  const ySafe = Number.isFinite(y) ? y : new Date().getFullYear();
  const branchTitle = MONTHLY_REPORT_BRANCH_TITLE[branchFilter] || branchFilter;
  const title = `MONTHLY REPORT - ${REPORT_TITLE_MONTHS[mi]} ${ySafe} - ${branchTitle}`;
  const { start: curStart, end: curEnd } = monthWindow(ySafe, mi);
  const { start: prevStart, end: prevEnd } = prevMonthWindow(ySafe, mi);

  if (importOnly && dateScope === "full" && !skipPrecalc) {
    const cacheKey = `${ySafe}-${mi}-${branchFilter}`;
    const cached = importMonthlyReportCache.get(cacheKey);
    if (cached) return overlayImportTopSales(cached, ySafe, mi, branchFilter);

    try {
      const { loadMonthlyImportPrecalc } = await import("./monthlyImportPrecalcService");
      const precalc = await loadMonthlyImportPrecalc({
        year: ySafe,
        monthIndex: mi,
        branchFilter,
      });
      if (precalc?.report) {
        const report =
          branchFilter === "Branch 3" && precalc.byBrand?.length
            ? patchBranch3ImportReportFromPrecalc(precalc.report, precalc.byBrand)
            : precalc.report;
        importMonthlyReportCache.set(cacheKey, report);
        return overlayImportTopSales(report, ySafe, mi, branchFilter);
      }
    } catch {
      /* fall through to live aggregation */
    }
  }

  let inRangeRows;
  let holidaySnap;
  let creditBills;
  let creditPayments;

  if (importOnly) {
    holidaySnap = { docs: [] };
    creditBills = [];
    creditPayments = [];
    if (dateScope === "current") {
      inRangeRows = await loadImportRowsForDateSpan(curStart, curEnd, branchFilter);
    } else {
      const [prevRows, curRows] = await Promise.all([
        loadImportRowsForDateSpan(prevStart, prevEnd, branchFilter, {
          excludeCreditNotes: branchFilter === "Main",
        }),
        loadImportRowsForDateSpan(curStart, curEnd, branchFilter),
      ]);
      inRangeRows = prevRows.concat(curRows);
    }
  } else {
    [inRangeRows, holidaySnap, creditBills, creditPayments] = await Promise.all([
      loadImportRowsForDateSpan(prevStart, curEnd, branchFilter),
      getDocs(collection(db, "holidays")),
      fetchAllCreditBills(),
      fetchAllPayments(),
    ]);
  }

  const invoices = mapImportRowsToInvoiceDocs(inRangeRows);

  const codesForItems = importOnly
    ? []
    : [
        ...collectInvoiceLineCodes(invoices),
        ...collectCreditNotePartNos(inRangeRows),
      ];
  const itemDocs = codesForItems.length ? await fetchItemDocsByCodes(codesForItems) : [];

  const codeToBrand = {};
  /** Raw Firestore `brand` (e.g. "OE HERO") for OE vs OTHER; line `it.brand` overrides when present. */
  const codeToRawBrand = {};
  for (const doc of itemDocs) {
    const d = doc.data() || {};
    const code = (d.code || "").toString().trim();
    if (!code) continue;
    const raw = d.brand != null ? String(d.brand).trim() : "";
    codeToRawBrand[code] = raw;
    codeToBrand[code] = normalizeBrand(raw);
  }

  const zBr = () => Object.fromEntries(MONTHLY_AUTOS_BRANDS.map((b) => [b, 0]));

  const curr = {
    cashSale: 0,
    creditBill: 0,
    otherSale: 0,
    outwardTotal: 0,
    lessSR: 0,
    oeAmt: 0,
    ordinaryAmt: 0,
    saleByBrand: zBr(),
    purchaseByBrand: zBr(),
    oeByBrand: zBr(),
    otherByBrand: zBr(),
    miscSale: 0,
    miscPurchase: 0,
    branchTransferOut: 0,
    branchTransferIn: 0,
    spareway: 0,
    saleCollection: 0,
    lessCashDiscount: 0,
    saleReturnByBrand: zBr(),
    saleReturnOeByBrand: zBr(),
    saleReturnOrdByBrand: zBr(),
  };

  const prev = {
    saleByBrand: zBr(),
    purchaseByBrand: zBr(),
    miscPurchase: 0,
    daySaleNumerator: 0,
    branchTransferOut: 0,
    branchTransferIn: 0,
  };

  const holidaySetCurrMonth = buildHolidayYmdSetFromDocs(
    holidaySnap.docs,
    curStart,
    curEnd
  );
  const holidaySetPrevMonth = buildHolidayYmdSetFromDocs(
    holidaySnap.docs,
    prevStart,
    prevEnd
  );
  const currDayCount = Math.max(
    1,
    getWorkingDayYmdsFullMonth(new Date(ySafe, mi, 1), holidaySetCurrMonth).length
  );
  const prevDayCount = Math.max(
    1,
    getWorkingDayYmdsFullMonth(new Date(prevStart.getFullYear(), prevStart.getMonth(), 1), holidaySetPrevMonth).length
  );

  const creditInvoiceIds = new Set();
  let importOsCsBillTotal = 0;
  let importOsCsLineTotal = 0;

  for (const inv of invoices) {
    const invBranch = inferInvoiceBranch(inv);
    if (invBranch !== branchFilter) continue;

    const d = parseInvoiceDate(inv);
    if (!d) continue;

    const t = String(inv.type || "").toLowerCase();
    const isInterState = Boolean(inv.isInterState);
    const gt = Number(inv.grandTotal ?? inv.totals?.net ?? 0) || 0;
    const cc = String(inv.cashCredit || "CASH").toUpperCase();
    const tc = (inv.transactionClass || "").toString().toUpperCase();

    const inCurr = inRange(d, curStart, curEnd);
    const inPrev = inRange(d, prevStart, prevEnd);

    const transferRow = { voucherType: inv.transactionClass };
    const isBranchTransferClass = isBranchTransferVoucherRow(transferRow);
    if (isBranchTransferClass) {
      const out = isTransferOutDirectionVoucherRow(transferRow);
      const inn = isTransferInDirectionVoucherRow(transferRow);
      if (inCurr) {
        if (out) curr.branchTransferOut += gt;
        if (inn) {
          curr.branchTransferIn += gt;
          if (importOnly && branchFilter === "Branch 3") {
            addBranch3TransferInToPurchaseBucket(curr, inv, codeToBrand, codeToRawBrand);
          }
        }
      }
      if (inPrev) {
        if (out) prev.branchTransferOut += gt;
        if (inn) {
          prev.branchTransferIn += gt;
          // Same rule as current month: brand purchase = inward + transfer IN.
          // So May current (May report) matches June previous (June report).
          if (importOnly && branchFilter === "Branch 3") {
            addBranch3TransferInToPurchaseBucket(prev, inv, codeToBrand, codeToRawBrand);
          }
        }
      }
    }

    if (inCurr && (tc.includes("SPAREWAY") || tc.includes("SPARE WAY"))) {
      curr.spareway += gt;
    }

    if (isCreditNoteVoucherType(tc)) continue;

    const skipAsSale =
      importOnly && branchFilter === "Main" && isMainBranchTransferOutPurchaseInvoice(inv);

    if (t === "outward" && inCurr && !skipAsSale) {
      const importOsCs =
        importOnly &&
        ((branchFilter === "Main" &&
          isMainBranchImportSaleVoucherRow({
            voucherType: inv.transactionClass ?? inv.voucherType ?? "",
          })) ||
          (branchFilter === "Wholesale" &&
            isWholesaleImportSaleVoucherRow({
              voucherType: inv.transactionClass ?? inv.voucherType ?? "",
            })));
      const { saleAmt, billTotal } = importOsCs
        ? importOsCsSaleAmounts(inv, gt)
        : { saleAmt: gt, billTotal: gt };

      if (importOsCs) {
        importOsCsBillTotal += billTotal;
        importOsCsLineTotal += saleAmt;
      }

      curr.outwardTotal += billTotal;

      if (cc === "CASH") {
        curr.cashSale += saleAmt;
        curr.saleCollection += invoiceAmountPaid(inv);
      } else if (cc === "CREDIT") {
        curr.creditBill += saleAmt;
        if (inv.id) creditInvoiceIds.add(inv.id);
      } else {
        curr.otherSale += saleAmt;
        curr.saleCollection += invoiceAmountPaid(inv);
      }

      const itemsList = inv.items || [];
      if (itemsList.length === 0) {
        const kind = classifySaleKind(inv);
        const tcBrand = brandFromTransactionClass(inv);
        if (tcBrand) {
          curr.saleByBrand[tcBrand] += saleAmt;
          if (kind === "oe") {
            curr.oeByBrand[tcBrand] += saleAmt;
          } else {
            curr.otherByBrand[tcBrand] += saleAmt;
          }
        } else {
          curr.miscSale += saleAmt;
        }
        if (kind === "oe") curr.oeAmt += saleAmt;
        else curr.ordinaryAmt += saleAmt;
      } else {
        for (const it of itemsList) {
          const code = lineCode(it);
          const rawBrand = rawBrandForOeLine(it, code, codeToRawBrand);
          const brand = brandForLine(it, code, codeToBrand, codeToRawBrand);
          const share = allocateLineShare(inv, it, isInterState);
          const oeB = oeBucketForImportLine(rawBrand, brand);

          if (brand === "MISCELLANEOUS") {
            curr.miscSale += share;
            continue;
          }
          if (!MONTHLY_AUTOS_BRANDS.includes(brand)) continue;

          curr.saleByBrand[brand] += share;
          if (oeB != null) {
            curr.oeByBrand[oeB] += share;
            curr.oeAmt += share;
          } else {
            curr.otherByBrand[brand] += share;
            curr.ordinaryAmt += share;
          }
        }
      }
    }

    if (t === "outward" && inPrev && !skipAsSale) {
      prev.daySaleNumerator += gt;
      const prevItems = inv.items || [];
      // Match current-month rules: use line items whenever present. Older code tested
      // transactionClass first and skipped lines, so multi-brand invoices could show
      // almost all previous-month sale under one brand or miss lines entirely.
      if (prevItems.length) {
        for (const it of prevItems) {
          const code = lineCode(it);
          const brand = brandForLine(it, code, codeToBrand, codeToRawBrand);
          const share = allocateLineShare(inv, it, isInterState);
          if (brand === "MISCELLANEOUS") continue;
          if (!MONTHLY_AUTOS_BRANDS.includes(brand)) continue;
          prev.saleByBrand[brand] += share;
        }
      } else {
        const tcBrand = brandFromTransactionClass(inv);
        if (tcBrand) {
          prev.saleByBrand[tcBrand] += gt;
        }
      }
    }

    if (t === "inward" && inCurr) {
      for (const it of inv.items || []) {
        const code = lineCode(it);
        const brand = brandForLine(it, code, codeToBrand, codeToRawBrand);
        const share = allocateLineShare(inv, it, isInterState);
        if (brand === "MISCELLANEOUS") curr.miscPurchase += share;
        else if (MONTHLY_AUTOS_BRANDS.includes(brand)) {
          curr.purchaseByBrand[brand] += share;
        }
      }
    }

    if (t === "inward" && inPrev) {
      for (const it of inv.items || []) {
        const code = lineCode(it);
        const brand = brandForLine(it, code, codeToBrand, codeToRawBrand);
        const share = allocateLineShare(inv, it, isInterState);
        if (brand !== "MISCELLANEOUS" && MONTHLY_AUTOS_BRANDS.includes(brand)) {
          prev.purchaseByBrand[brand] += share;
        }
      }
    }

    if (skipAsSale && inCurr) {
      subtractPurchaseByBrand(curr, inv, isInterState, codeToBrand, codeToRawBrand);
    }
    if (skipAsSale && inPrev) {
      subtractPurchaseByBrand(prev, inv, isInterState, codeToBrand, codeToRawBrand);
    }

  }

  accumulateImportCreditNoteSaleReturns(inRangeRows, {
    branchFilter,
    curStart,
    curEnd,
    curr,
    codeToBrand,
    codeToRawBrand,
  });

  if (importOnly && branchFilter === "Main" && importOsCsBillTotal > 0) {
    const billLineGap = roundMoney(importOsCsBillTotal - importOsCsLineTotal);
    curr.otherSale = billLineGap > 0 ? billLineGap : 0;
  }

  // Credit bills (Udhar) — include when invoice row was not tagged CREDIT or bill is manual.
  if (!importOnly) for (const bill of creditBills) {
    if (creditBillBranch(bill) !== branchFilter) continue;
    const d = parseInvoiceDate({
      invoiceDateStr: bill.invoiceDate,
      createdAt: bill.createdAt,
    });
    if (!d || !inRange(d, curStart, curEnd)) continue;
    const invId = bill.invoiceId ? String(bill.invoiceId) : "";
    if (invId && creditInvoiceIds.has(invId)) continue;
    curr.creditBill += roundMoney(bill.totalAmount);
  }

  // Udhar receipts in this month (Credit / Due payments).
  if (!importOnly) for (const p of creditPayments) {
    if (inferPaymentBranch(p) !== branchFilter) continue;
    const d = paymentTimestamp(p);
    if (!d || !inRange(d, curStart, curEnd)) continue;
    curr.saleCollection += roundMoney(p.amount);
  }

  const totalOeOrd = curr.oeAmt + curr.ordinaryAmt;
  const oePct = totalOeOrd > 0 ? (curr.oeAmt / totalOeOrd) * 100 : 63;
  const ordinaryPct = totalOeOrd > 0 ? (curr.ordinaryAmt / totalOeOrd) * 100 : 24;

  const avgCurr = curr.outwardTotal / currDayCount;
  const avgPrev = prev.daySaleNumerator / prevDayCount;
  const avgPct = pctChange(avgPrev, avgCurr);

  const brands = MONTHLY_AUTOS_BRANDS.map((name) => {
    const prevSale = prev.saleByBrand[name] || 0;
    const currSale = curr.saleByBrand[name] || 0;
    const purchasePrev = prev.purchaseByBrand[name] || 0;
    const purchaseCurr = curr.purchaseByBrand[name] || 0;
    const sub = currSale;
    const oeAmtBrand = curr.oeByBrand[name] || 0;
    const otherAmtBrand = curr.otherByBrand[name] || 0;
    const oePctB = sub > 0 ? (oeAmtBrand / sub) * 100 : 55 + (MONTHLY_AUTOS_BRANDS.indexOf(name) * 3) % 20;
    const otherPctB = sub > 0 ? (otherAmtBrand / sub) * 100 : Math.max(0, 100 - oePctB);

    return {
      name,
      topSale: currSale,
      prevSale,
      currSale,
      salePct: pctChange(prevSale, currSale),
      purchasePrev,
      purchaseCurr,
      purchasePct: pctChange(purchasePrev, purchaseCurr),
      oePct: oePctB,
      otherPct: otherPctB,
      oeAmt: oeAmtBrand,
      otherAmt: otherAmtBrand,
      brandTotal: currSale,
    };
  });

  const totalSale = curr.cashSale + curr.creditBill + curr.otherSale;
  const totalPurchase = MONTHLY_AUTOS_BRANDS.reduce((s, b) => s + (curr.purchaseByBrand[b] || 0), 0) + curr.miscPurchase;

  const lessSaleReturn = curr.lessSR;
  const totalCashInHand =
    curr.cashSale + curr.saleCollection - curr.lessCashDiscount - lessSaleReturn;

  const report = {
    title,
    titleCorner: String(currDayCount),
    workingDays: {
      prev: prevDayCount,
      curr: currDayCount,
    },
    totals: {
      cashSale: curr.cashSale,
      creditBill: curr.creditBill,
      otherSale: curr.otherSale,
      lessSR: curr.lessSR,
    },
    avgDaySale: {
      prev: avgPrev,
      curr: avgCurr,
      pctChange: avgPct,
    },
    saleSplit: {
      oePct: Math.round(oePct * 100) / 100,
      ordinaryPct: Math.round(ordinaryPct * 100) / 100,
      oeAmt: curr.oeAmt,
      ordinaryAmt: curr.ordinaryAmt,
    },
    brands,
    collection: {
      cashSale: curr.cashSale,
      saleCollection: curr.saleCollection,
      lessCashDiscount: curr.lessCashDiscount,
      lessSaleReturn,
      totalCashInHandParens: Math.abs(totalCashInHand),
    },
    footer: {
      miscSale: curr.miscSale,
      miscPurchase: curr.miscPurchase,
      branchTransferOut: curr.branchTransferOut,
      branchTransferIn: curr.branchTransferIn,
      transferOutPrev: prev.branchTransferOut,
      transferOutCurr: curr.branchTransferOut,
      spareway: curr.spareway,
      totalSale: totalSale || curr.outwardTotal,
      totalPurchase,
      totalExpense: 0,
      expenseDirectTotal: 0,
      expenseIndirectTotal: 0,
      expenseLines: [],
    },
    saleReturnByBranch: curr.saleReturnByBrand,
    saleReturnByBrand: curr.saleReturnByBrand,
    saleReturnOeByBrand: curr.saleReturnOeByBrand,
    saleReturnOrdByBrand: curr.saleReturnOrdByBrand,
  };

  if (importOnly) {
    if (dateScope === "full") {
      importMonthlyReportCache.set(`${ySafe}-${mi}-${branchFilter}`, report);
    }
    return overlayImportTopSales(report, ySafe, mi, branchFilter);
  }

  return report;
}

async function overlayImportTopSales(report, year, monthIndex, branchFilter) {
  if (!report) return report;
  try {
    const { resolveMonthlyTopSales, applyTopSalesToReport } = await import(
      "./monthlyTopSalesService"
    );
    const resolved = await resolveMonthlyTopSales(year, monthIndex, branchFilter);
    applyTopSalesToReport(report, resolved.found ? resolved.values : {});
  } catch {
    applyZeroTopSales(report);
  }
  try {
    const { loadMonthlySaleCollection, applySaleCollectionToReport } = await import(
      "./monthlySaleCollectionService"
    );
    const savedCollection = await loadMonthlySaleCollection(year, monthIndex, branchFilter);
    if (savedCollection?.amount != null) {
      applySaleCollectionToReport(report, savedCollection.amount);
    }
  } catch {
    /* keep import-calculated collection */
  }
  return report;
}

function applyZeroTopSales(report) {
  if (!report?.brands?.length) return;
  for (const b of report.brands) b.topSale = 0;
}

/** Fixed labels for the printed form (amounts come from Firestore via saleReturnByBrand). */
const SALE_RETURN_LABELS = {
  HERO: "HERO SALE RETURN",
  HONDA: "HONDA SALE RETURN",
  BAJAJ: "BAJAJ SALE RETURN",
  BULLET: "BULLET SALE RETURN",
  TVS: "TVS,SUZUKI SALE RETURN",
  YAMAHA: "YAMAHA SALE RETURN",
  PIAGGIO: "PIAGGIO SALE RETURN",
};

/**
 * Maps fetchMonthlyAutosReport() output to MonthlySaleReportPage `DATA` shape.
 * @param {object|null} report
 * @returns {object|null}
 */
export function mapMonthlyAutosReportToSalePageData(report) {
  if (!report) return null;

  const cashSale = report.totals.cashSale;
  const creditBill = report.totals.creditBill;
  const otherSale = report.totals.otherSale;
  const saleCollection = report.collection.saleCollection;
  const lessCashDiscount = report.collection.lessCashDiscount ?? 0;
  const lessSaleReturn = report.collection.lessSaleReturn;
  const subtotal = cashSale + saleCollection + otherSale;
  const totalCashInHand = cashSale + saleCollection - lessCashDiscount - lessSaleReturn;

  const sr = report.saleReturnByBrand || {};
  const footer = report.footer || {};

  const brands = report.brands.map((b) => {
    const name = b.name;
    const saleReturnAmt = sr[name] ?? 0;

    if (name === "MAHINDRA") {
      return {
        n: name,
        top: null,
        p: b.prevSale,
        c: b.currSale,
        pct: b.salePct,
        pp: null,
        pc: null,
        rLabel: null,
        rAmt: null,
        oe: null,
        oeAmt: null,
        oth: null,
        othAmt: null,
      };
    }

    const rLabel =
      name === "SUZUKI" ? null : (SALE_RETURN_LABELS[name] || `${name} SALE RETURN`);
    const rAmt = name === "SUZUKI" ? null : saleReturnAmt;

    return {
      n: name,
      top: b.topSale,
      p: b.prevSale,
      c: b.currSale,
      pct: b.salePct,
      pp: b.purchasePrev,
      pc: b.purchaseCurr,
      rLabel,
      rAmt,
      oe: b.oePct,
      oeAmt: b.oeAmt,
      oth: b.otherPct,
      othAmt: b.otherAmt,
    };
  });

  return {
    title: report.title,
    corner: report.titleCorner,
    cashSale,
    creditBill,
    otherSale,
    saleCollection,
    subtotal,
    avgPrev: report.avgDaySale.prev,
    avgCurr: report.avgDaySale.curr,
    avgPct: report.avgDaySale.pctChange,
    workingDaysPrev: report.workingDays?.prev ?? 1,
    workingDaysCurr: report.workingDays?.curr ?? 1,
    lessSaleReturn,
    totalSale: footer.totalSale,
    totalCashInHand,
    oePct: Math.round(report.saleSplit.oePct),
    ordinaryPct: Math.round(report.saleSplit.ordinaryPct),
    oeAmt: report.saleSplit.oeAmt,
    ordinaryAmt: report.saleSplit.ordinaryAmt,
    brands,
    miscSale: footer.miscSale,
    miscPurchase: footer.miscPurchase,
    transferOutPrev: footer.transferOutPrev ?? 0,
    transferOutCurr: footer.transferOutCurr ?? footer.branchTransferOut ?? 0,
    transferIn: footer.branchTransferIn,
    totalPurchase: footer.totalPurchase,
    totalExpense: footer.totalExpense ?? 0,
    expenseDirectTotal: footer.expenseDirectTotal ?? 0,
    expenseIndirectTotal: footer.expenseIndirectTotal ?? 0,
    expenseLines: Array.isArray(footer.expenseLines) ? footer.expenseLines : [],
    /** Per-brand totals (credit notes + manual upload). */
    saleReturnByBrand: { ...sr },
    /** Manual monthly upload: OE-attributed sales return rows (mfr prefix OE). */
    saleReturnOeByBrand: { ...(report.saleReturnOeByBrand || {}) },
    /** Manual monthly upload: ordinary sales return rows. */
    saleReturnOrdByBrand: { ...(report.saleReturnOrdByBrand || {}) },
  };
}
